import React from 'react';
import {
  Box,
  Image,
  Text,
  ColorPreview,
  Separator,
  IconCar,
} from '@acv/clearcar-component-lib';
import styled from '@emotion/styled';
import {
  FetchUnReadNotificationsGql,
  NotificationUpdateReadGql,
} from '@portal/query/graphql';
import { useLocale, useMutation } from '@portal/hooks';
import { Route } from '@portal/lib';
import {
  Thumbnail,
  NotificationUnReadColor,
  generateNotificationTime,
  getVehicleName,
} from './lib';
import useNotifications from '@portal/vehicle/hooks/useNotifications';
import UploadIcon from '@portal/static/icons/upload_ready.svg';

const NotificationRow = ({ notification, showPanelNotification }) => {
  const {
    created_at,
    vehicle_id,
    read,
    vehicle = {},
    id,
    file_url,
  } = notification || {};
  const { locale } = useLocale();

  const { fetchNotifications } = useNotifications();

  const [updateNotificationRead] = useMutation(NotificationUpdateReadGql, {
    isSuccessAlertMuted: true,
    refetchQueries: [
      {
        query: FetchUnReadNotificationsGql,
      },
    ],
    onCompleted: () => {
      fetchNotifications();
    },
  });

  let isBUnotification = false;
  if (!vehicle && file_url?.length > 0) {
    isBUnotification = true;
  }

  const notificationTime = generateNotificationTime(created_at);
  const leadDetailPath = Route?.leadList.path;
  const redirectLink = isBUnotification
    ? file_url
    : `${leadDetailPath}/${vehicle_id}`;

  const handleNotificationClick = () => {
    if (!read) {
      const variables = {
        id: Number(id),
        read: true,
      };
      updateNotificationRead({ variables });
    }
    window.open(redirectLink, '_blank');
  };

  if (isBUnotification) {
    return (
      <>
        <Box paddingVertical="md" align="center" justify="space-between" data-test-id={`e2e-notification-row-bulk-upload-${id}`}>
          <LinkBox
            flexGap="lg"
            align="center"
            onClick={handleNotificationClick}
            data-testid="e2e-notification-row-bulk-upload-link"
          >
            <UploadIcon width={Thumbnail.width} height={Thumbnail.height} data-testid="e2e-notification-row-bulk-upload-icon" />
            <Box isFlex direction={showPanelNotification ? 'column' : 'row'} data-testid="e2e-notification-row-bulk-upload-content">
              <Text
                weight="semibold"
                color="gray-text"
                marginLeft={!showPanelNotification ? 'xs' : ''}
                data-testid="e2e-notification-row-bulk-upload-complete-text"
              >
                {locale.notificationLeadsUploadComplete}
              </Text>
              <Text
                size="sm"
                style={{
                  color: 'red',
                  marginLeft: !showPanelNotification ? 10 : 0,
                }}
                marginBottom="xs"
                data-testid="e2e-notification-row-bulk-upload-failed-text"
              >
                {locale.notificationUploadFailed}
              </Text>
              {showPanelNotification && (
                <Text color="primary" data-testid="e2e-notification-row-bulk-upload-time-panel">{notificationTime}</Text>
              )}
            </Box>
          </LinkBox>
          <TimestampBox
            flexGap="xl"
            justify="flex-start"
            alignself={showPanelNotification}
            data-testid="e2e-notification-row-bulk-upload-timestamp"
          >
            {!showPanelNotification && (
              <Text color="primary" data-testid="e2e-notification-row-bulk-upload-time">{notificationTime}</Text>
            )}
            {!read && <ColorPreview hex={NotificationUnReadColor} data-testid="e2e-notification-row-bulk-upload-unread-indicator" />}
          </TimestampBox>
        </Box>
        {!showPanelNotification && <Separator data-testid="e2e-notification-row-bulk-upload-separator" />}
      </>
    );
  }

  const {
    customer_first_name,
    customer_last_name,
    year,
    make,
    model,
    trim,
    vehicle_images,
  } = vehicle || {};
  const name = `${customer_first_name} ${customer_last_name}`.trim();
  const notificationMsg = name
    ? `${name} ${locale.notificationPhotoUploadedFor}`
    : locale.notificationPhotosHaveBeenUploadedFor;

  const thumbnailURL = vehicle_images?.[0]?.link;
  const vehicleName = getVehicleName({ year, make, model, trim });

  return (
    <>
      <Box paddingVertical="md" align="center" justify="space-between" data-test-id={`e2e-notification-row-vehicle-${vehicle_id}`}>
        <LinkBox flexGap="lg" align="center" onClick={handleNotificationClick} data-testid="e2e-notification-row-vehicle-link">
          <Image
            hasError={!thumbnailURL}
            src={thumbnailURL}
            alt="Notification"
            placeholder={<IconCar />}
            width={Thumbnail.width}
            height={Thumbnail.height}
            radius="md"
            data-testid="e2e-notification-row-vehicle-image"
          />
          <Box isFlex direction={showPanelNotification ? 'column' : 'row'} data-testid="e2e-notification-row-vehicle-content">
            <Text size="sm" color="gray-text" marginBottom="xs" data-testid="e2e-notification-row-vehicle-message">
              {notificationMsg}
            </Text>
            <Text
              weight="semibold"
              color="gray-text"
              marginLeft={!showPanelNotification ? 'xs' : ''}
              data-testid="e2e-notification-row-vehicle-name"
            >
              {vehicleName}
            </Text>
            {showPanelNotification && (
              <Text color="primary" data-testid="e2e-notification-row-vehicle-time-panel">{notificationTime}</Text>
            )}
          </Box>
        </LinkBox>
        <TimestampBox
          flexGap="xl"
          justify="flex-start"
          alignself={showPanelNotification}
          data-testid="e2e-notification-row-vehicle-timestamp"
        >
          {!showPanelNotification && (
            <Text color="primary" data-testid="e2e-notification-row-vehicle-time">{notificationTime}</Text>
          )}
          {!read && <ColorPreview hex={NotificationUnReadColor} data-testid="e2e-notification-row-vehicle-unread-indicator" />}
        </TimestampBox>
      </Box>
      {!showPanelNotification && <Separator data-testid="e2e-notification-row-vehicle-separator" />}
    </>
  );
};

export default NotificationRow;

const LinkBox = styled(Box)`
  cursor: pointer;
`;

const TimestampBox = styled(Box)`
  ${({ alignself }) =>
    alignself &&
    `
    align-self: baseline;
  `}
`;
